import { Component, OnInit, inject } from '@angular/core';
import { PoliticsModeService } from './services/politics-mode.service';
import { AppThemeService } from './services/app-theme.service';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngrx/store';
import { NavigationError, Router } from '@angular/router';
import { AlertController, ToastController, Platform, ModalController } from '@ionic/angular';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { StatusBar, Style } from '@capacitor/status-bar';

import { AppState } from './store';
import { ToastIcons } from './app.constants';
import { StorageService } from './shared/storage';
import { AuthClientService } from './client/auth-client';
import { Agent, AgentClientService, TermsOfUse } from './client/agent-client';
import { Confirm, MessageService } from './shared/message';
import * as SecurityAction from './store/security/security.action';
import * as AgentAction from './store/agent/agent.action';
import { ProfilePhotoStoreService } from './shared/profile-photo/profile-photo-store.service';
import { RefreshEvent, SecurityService } from './shared/security';
import { OneSignalService } from './services/onesignal.service';
import { CometChatService } from './services/cometchat.service';
import { InAppNotificationService } from './services/in-app-notification.service';
import { AppLockService } from './services/app-lock.service';
import { AppUsageService } from './services/app-usage.service';
import { PolicyModal } from './modals/policy/policy.page';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    standalone: false
})
export class AppComponent implements OnInit {
  private static readonly CHUNK_RELOAD_COOLDOWN_MS = 60_000;
  private static readonly CHUNK_RELOAD_MAX_ATTEMPTS = 3;
  private static readonly CHUNK_RELOAD_STORAGE_KEY = 'rsm-chunk-reload-tracker';
  private termsOfUseChecking = false;
  private politicsModeService = inject(PoliticsModeService);
  private appThemeService = inject(AppThemeService);
  private termsOfUseCheckedUserId?: string;

  constructor(
    private router: Router,
    private platform: Platform,
    private store: Store<AppState>,
    private storageService: StorageService,
    private messageService: MessageService,
    private alertController: AlertController,
    private toastController: ToastController,
    private modalController: ModalController,
    private securityService: SecurityService,
    private authClientService: AuthClientService,
    private agentClientService: AgentClientService,
    private oneSignalService: OneSignalService,
    private cometChatService: CometChatService,
    private inAppNotificationService: InAppNotificationService,
    public appLockService: AppLockService,
    private appUsageService: AppUsageService,
    private profilePhotoStoreService: ProfilePhotoStoreService
  ) { }

  ngOnInit(): void {
    this.initStatusBar();
    this.initCapacitorUpdater();
    this.initMessage();
    this.initInAppNotificationToasts();
    this.initRouterRecovery();
    this.initParamsAdaptiveLayout();
    this.oneSignalService.initialize();
    this.cometChatService.init();

    this.storageService.init().then(async () => {
      // Antes do initSecurity de propósito: pinta com a cor guardada da última
      // sessão, para o app não abrir azul e piscar para a cor do cliente quando
      // a resposta do servidor chegar.
      await this.appThemeService.applyStoredTheme();
      this.initSecurity();
      this.inAppNotificationService.loadPending();
    });
  }

  /**
   * Configura a status bar (horário, sinal, bateria) sem que o conteúdo fique
   * escondido sob ela.
   *
   * - Android (inclui edge-to-edge do Android 15): tira o overlay e dá um fundo
   *   branco sólido à status bar.
   * - iOS: a status bar SOBREPÕE a webview (transparente); o topo é reservado
   *   pela safe area das páginas (padding). NÃO usar overlay:false no iOS —
   *   o plugin pinta a área da status bar com o backgroundColor padrão (preto)
   *   e some com horário/sinal/bateria (ícones escuros sobre fundo preto).
   */
  private async initStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
      } else {
        await StatusBar.setOverlaysWebView({ overlay: true });
      }
      await StatusBar.setStyle({ style: Style.Light });
    } catch {
      // StatusBar pode não estar disponível em alguns contextos — ignora.
    }
  }

  private initCapacitorUpdater(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    CapacitorUpdater.notifyAppReady();

    CapacitorUpdater.addListener('download', async (info: any) => {
      if (info.percent === 100) {
        const toast = await this.toastController.create({
          color: 'success',
          mode: 'ios',
          duration: 4000,
          position: 'top',
          message: 'Atualização baixada. Será aplicada no próximo reinício.',
          cssClass: 'toast-custom',
          buttons: [{ side: 'end', icon: 'close-outline' }],
        });
        toast.present();
      }
    });

    CapacitorUpdater.addListener('updateFailed', async () => {
      const toast = await this.toastController.create({
        color: 'danger',
        mode: 'ios',
        duration: 4000,
        position: 'top',
        message: 'Falha ao aplicar a atualização do aplicativo.',
        cssClass: 'toast-custom',
        buttons: [{ side: 'end', icon: 'close-outline' }],
      });
      toast.present();
    });

    CapacitorUpdater.addListener('majorAvailable', async (info: any) => {
      const alert = await this.alertController.create({
        header: 'Nova versão disponível',
        message: `A versão ${info.version} está disponível na loja de aplicativos. Atualize para continuar recebendo melhorias.`,
        backdropDismiss: false,
        buttons: ['OK'],
      });
      alert.present();
    });
  }

  private initRouterRecovery(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError
        )
      )
      .subscribe((event) => {
        const errorName = (event.error as any)?.name;
        const errorMessage = String((event.error as any)?.message || '');

        if (
          errorName === 'ChunkLoadError' ||
          errorMessage.includes('Loading chunk')
        ) {
          this.handleChunkLoadError();
        }
      });
  }

  private handleChunkLoadError(): void {
    try {
      const raw = window.localStorage.getItem(AppComponent.CHUNK_RELOAD_STORAGE_KEY);
      const tracker = raw ? JSON.parse(raw) : { count: 0, lastAt: 0 };
      const now = Date.now();

      // Reseta contador se já passou o cooldown (evita loop eterno).
      if (now - tracker.lastAt > AppComponent.CHUNK_RELOAD_COOLDOWN_MS) {
        tracker.count = 0;
      }

      tracker.count += 1;
      tracker.lastAt = now;
      window.localStorage.setItem(AppComponent.CHUNK_RELOAD_STORAGE_KEY, JSON.stringify(tracker));

      if (tracker.count <= AppComponent.CHUNK_RELOAD_MAX_ATTEMPTS) {
        window.location.reload();
      } else {
        console.error('[App] ChunkLoadError persistente. Desistindo de recarregar automaticamente.');
      }
    } catch {
      // Fallback seguro: recarrega uma vez se não conseguir ler o localStorage.
      window.location.reload();
    }
  }

  private initSecurity(): void {
    this.securityService.onUnauthorized.subscribe((event) => {
      this.securityService.invalidate().subscribe();

      if (event?.message) {
        this.messageService.addMsgDanger({ message: event?.message });
      }
    });

    this.securityService.onInvalidate.subscribe(() => {
      this.termsOfUseCheckedUserId = undefined;
      this.cometChatService.logout();
      // Limpa caches em memoria que sobrevivem ao logout (singletons/NgRx),
      // senao o proximo usuario ve a foto/dados do anterior ate recarregar.
      this.profilePhotoStoreService.clear();
      // O modo Politics é por usuário: sem isto o próximo login herdaria o
      // ambiente escolhido pelo anterior.
      this.politicsModeService.reset();
      // A cor e da empresa: sem isto o proximo login herdaria a marca do anterior.
      this.appThemeService.reset();
      this.store.dispatch(AgentAction.clean());
      this.goToLoginAfterLogout();
    });

    this.securityService.onRefresh.subscribe((event: RefreshEvent) => {
      const { refreshToken } = event.credential;

      this.authClientService.refresh(refreshToken).subscribe(
        (credential: any) => {
          if (credential.cometChatAuthToken) {
            this.cometChatService.login(credential.cometChatAuthToken);
          }
          event.next(credential);
        },
        (error) => {
          event.error(error);
        }
      );
    });

    this.securityService.init().subscribe(
      () => {
        this.store.dispatch(SecurityAction.available({ available: true }));
        const cred = this.securityService.credential;
        if (cred && cred.cometChatAuthToken) {
          this.cometChatService.login(cred.cometChatAuthToken);
        }
        // Reidentifica o device no OneSignal com o agent.id (ver método).
        this.reidentifyPushDevice();
        // App-lock: exige biometria ao abrir/retornar quando há sessão + biometria habilitada.
        this.appLockService.init();
        // Rastreio de tempo de uso do app (KPIs "Último acesso" / "Tempo ativo").
        this.appUsageService.init();
        // Cor da empresa no login, como pede o criterio de aceite. Vem depois do
        // tema local ja aplicado, entao so corrige se o cliente mudou a cor.
        this.appThemeService.refresh().subscribe();
        this.checkPendingTermsOfUse();
      },
      () => this.store.dispatch(SecurityAction.available({ available: true }))
    );
  }

  private checkPendingTermsOfUse(): void {
    const credential = this.securityService.credential;
    if (!credential?.userId || credential.temporaryPassword || this.termsOfUseChecking) {
      return;
    }

    if (this.termsOfUseCheckedUserId === credential.userId) {
      return;
    }

    this.termsOfUseChecking = true;
    this.agentClientService.getPendingAppTermsOfUse().subscribe({
      next: (terms) => {
        this.termsOfUseCheckedUserId = credential.userId;
        this.termsOfUseChecking = false;
        this.openPendingTermModal(terms || [], 0);
      },
      error: () => {
        this.termsOfUseChecking = false;
      },
    });
  }

  private async openPendingTermModal(terms: TermsOfUse[], index: number): Promise<void> {
    const term = terms[index];
    if (!term) {
      return;
    }

    const modal = await this.modalController.create({
      component: PolicyModal,
      componentProps: {
        term,
        readonly: false,
      },
      backdropDismiss: false,
      initialBreakpoint: 1,
      breakpoints: [1],
    });

    modal.onDidDismiss().then(({ data }) => {
      if (data === true) {
        this.openPendingTermModal(terms, index + 1);
      }
    });

    await modal.present();
  }

  /**
   * Reidentifica o device no OneSignal a cada abertura com sessão ativa.
   *
   * Necessário porque o External ID fica PERSISTIDO no SDK: os devices que
   * logaram entre 02/06 e a correção ficaram gravados com o userId (regressão
   * do bab8e34) e, sem isso, só voltariam a receber push no próximo login
   * manual. Chamar aqui faz a auto-cura na primeira abertura.
   *
   * É idempotente (relogar o mesmo External ID é no-op), roda só com sessão
   * válida e falha em silêncio — nunca deve atrapalhar a abertura do app.
   */
  private reidentifyPushDevice(): void {
    const credential = this.securityService.credential;
    if (!credential?.userId) {
      return;
    }

    this.agentClientService.getByUserId(credential.userId).subscribe({
      next: (agent: Agent) => {
        if (agent?.id) {
          this.oneSignalService.login(agent.id, {
            network_type: agent.context?.context ?? undefined,
          });
        }
      },
      error: () => {},
    });
  }

  /**
   * No logout, fecha quaisquer overlays abertos (ex: o modal do menu de perfil)
   * ANTES de navegar para o login. Sem isso o modal fica órfão por cima da tela
   * de login e o app parece travado.
   */
  private async goToLoginAfterLogout(): Promise<void> {
    await this.dismissTopOverlays();
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  private async dismissTopOverlays(): Promise<void> {
    for (const ctrl of [this.modalController, this.alertController]) {
      let guard = 0;
      let top = await ctrl.getTop();
      while (top && guard < 5) {
        await top.dismiss().catch(() => {});
        top = await ctrl.getTop();
        guard++;
      }
    }
  }

  private initMessage(): void {
    this.messageService.onError.subscribe((error) => {
      if (error?.status === 401 || error?.status === 403) {
        return;
      }

      this.router.navigate(['/error']);
    });

    this.messageService.onConfirm.subscribe(async (confirm: Confirm) => {
      const alert = await this.alertController.create({
        ...confirm,
        backdropDismiss: false,
      });
      await alert.present();
    });

    this.messageService.onMessage.subscribe(async (event: MessageEvent) => {
      const { type, data } = event;

      if (data.status === 401 || data.status === 403) {
        return;
      }

      const toast = await this.toastController.create({
        color: type,
        mode: 'ios',
        duration: 5000,
        position: 'top',
        message: data.message,
        cssClass: 'toast-custom',
        buttons: [
          { side: 'start', icon: ToastIcons[type] },
          { side: 'end', icon: 'close-outline' },
        ],
      });

      toast.present();
    });
  }

  private initInAppNotificationToasts(): void {
    this.inAppNotificationService.notifications$
      .pipe(
        filter((notifications) => notifications.length > 0),
        distinctUntilChanged((prev, curr) =>
          prev.length === curr.length && prev.every((n, i) => n.id === curr[i].id)
        )
      )
      .subscribe((notifications) => {
        this.showInAppToasts(notifications);
      });
  }

  private showInAppToasts(notifications: any[]): Promise<void> {
    return notifications.reduce((chain, n) => {
      return chain.then(() => this.presentInAppToast(n));
    }, Promise.resolve());
  }

  private async presentInAppToast(notification: any): Promise<void> {
    const toast = await this.toastController.create({
      color: 'success',
      mode: 'ios',
      duration: 4000,
      position: 'top',
      message: this.inAppNotificationService.labelFor(notification.type),
      cssClass: 'toast-custom',
      buttons: [
        { side: 'start', icon: this.inAppNotificationService.iconFor(notification.type) },
        { side: 'end', icon: 'close-outline' },
      ],
    });
    await toast.present();
    await toast.onDidDismiss();
  }

  private initParamsAdaptiveLayout(): void {
    if (!this.platform.is('android') && !this.platform.is('ios')) {
      return;
    }

    const { documentElement } = document;
    const fontSize = Math.round((12 * window.innerHeight) / 640);

    documentElement.style.setProperty('--rsm-font-size', `${fontSize}px`);
    documentElement.style.setProperty(
      '--rsm-inner-width',
      `${window.innerWidth}px`
    );
    documentElement.style.setProperty(
      '--rsm-inner-height',
      `${window.innerHeight}px`
    );
  }
}
